// Graph intermittently rejects a membership write while the same group is being
// modified concurrently (bulk add/remove), or when a just-created member hasn't
// replicated yet, with "Resource '…' does not exist or one of its queried
// reference-property objects are not present" — even though the member is (or will
// shortly be) there; the write succeeds once the contending call settles / replication
// catches up. It also throttles with 429/503. The roles- and acls-page mutations use
// the plain `query` builder, so (unlike the queries) they get no retry — retry these
// transient failures here with a short backoff before surfacing the error. See issue
// #67 (bulk-remove report), #83 (the ACLs page lacked this retry) and #87 (the
// reference-not-present case can arrive as 400, not just 404).
const MAX_SUBMIT_RETRIES = 3;
const SUBMIT_RETRY_BASE_MS = 300;
// The reference-not-present signature. Graph returns it as 404 for the concurrent-
// modification race and as 400 (Request_BadRequest) when a `$ref` body names a member
// that hasn't replicated yet — so match on the MESSAGE regardless of status (#87).
const REFERENCE_NOT_PRESENT = /does not exist|are not present/i;
// Re-adding a member that is already there comes back as 400 "One or more added object
// references already exist…". For an idempotent resume that outcome IS success, so a
// caller retrying a half-finished run can treat it as a no-op rather than an error. Match
// the full "object references already exist" phrase so an unrelated message that merely
// contains "references already exist" can't be mistaken for it and silently swallowed.
const ALREADY_MEMBER = /object references already exist/i;

const errorStatus = (error: unknown) => (error as { status?: number | string })?.status;
export const errorMessage = (error: unknown) =>
  (error as { data?: { error?: { message?: string } } })?.data?.error?.message;

// Shared predicate for "retry this Graph membership write" — throttling (429/503) or the
// replication/concurrent-modification reference-not-present signature (400 or 404). Used
// by both withTransientRetry (here) and addWithRetry (group-creation flows) so the two
// can't drift on what counts as transient. See #87.
export const isTransientGraphError = (error: unknown) => {
  const status = errorStatus(error);
  if (status === 429 || status === 503) return true;
  const message = errorMessage(error);
  return !!message && REFERENCE_NOT_PRESENT.test(message);
};

// True when a membership write failed only because the member is already present — the
// idempotent-success case for a resumed/repaired provisioning run (#87).
export const isAlreadyMember = (error: unknown) => {
  const message = errorMessage(error);
  return !!message && ALREADY_MEMBER.test(message);
};

// Run one Graph membership write, retrying the transient concurrent-modification 404
// and throttling (429/503) with a short escalating backoff; rethrows once the retries
// are exhausted or the error isn't transient, so callers toast/leave-staged as usual.
export const withTransientRetry = async <T>(op: () => Promise<T>): Promise<T> => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await op();
    } catch (error) {
      if (attempt < MAX_SUBMIT_RETRIES && isTransientGraphError(error)) {
        await new Promise((resolve) => setTimeout(resolve, SUBMIT_RETRY_BASE_MS * (attempt + 1)));
        continue;
      }
      throw error;
    }
  }
};
